// CB:BEGIN modern-api-wrapper
import { useAuth } from "@clerk/clerk-react";

export interface UserProfile {
  userId: string;
  role: string;
  email: string | null;
}

// CB:BEGIN fix-tdz-request-cache
// Request deduplication cache - defined after imports to avoid TDZ
const requestCache = new Map<string, Promise<any>>();
// CB:END fix-tdz-request-cache

type RequestOptions = RequestInit & { dedupeKey?: string | false };

export function useApi() {
  const { getToken } = useAuth();

  async function request<T = any>(path: string, init: RequestOptions = {}): Promise<T> {
    // CB:BEGIN request-deduplication
    const { dedupeKey, ...rest } = init as RequestInit & { dedupeKey?: string | false };
    const cacheKey = dedupeKey === false
      ? undefined
      : (typeof dedupeKey === 'string' ? dedupeKey : `${rest.method || 'GET'}:${path}:${JSON.stringify(rest.body || {})}`);
    
    // If the same request is already in progress, return the existing promise
    if (cacheKey && requestCache.has(cacheKey)) {
      console.log('🔄 Deduplicating request:', path);
      return requestCache.get(cacheKey);
    }
    
    // Create the request promise
    const requestPromise = makeRequest<T>(path, rest);
    
    // Cache the promise
    if (cacheKey) {
      requestCache.set(cacheKey, requestPromise);
      
      // Clean up the cache when the request completes
      requestPromise.finally(() => {
        requestCache.delete(cacheKey);
      });
    }
    
    return requestPromise;
    // CB:END request-deduplication
  }

  async function makeRequest<T = any>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers || {});
    
    // CB:BEGIN development-auth-token
    // Check for development authentication first
    let token = null;
    try {
      const devAuth = localStorage.getItem('devAuth');
      if (devAuth) {
        const { token: devToken, isDevelopment } = JSON.parse(devAuth);
        if (isDevelopment && devToken) {
          token = devToken;
          console.log('🔧 Using development authentication token');
        }
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    
    // Fallback to Clerk token if no development token
    if (!token) {
      token = await getToken();
    }
    
    if (token) headers.set("Authorization", `Bearer ${token}`);
    // CB:END development-auth-token

    // CB:BEGIN impersonation-token-header
    // If an admin is in an impersonation session, attach the token so the backend
    // can audit-log actions taken on behalf of a user.
    try {
      const raw = sessionStorage.getItem('cb_impersonation');
      if (raw) {
        const parsed = JSON.parse(raw);
        const TTL_MS = 30 * 60 * 1000;
        if (parsed?.token && parsed?.startedAt && Date.now() - parsed.startedAt < TTL_MS) {
          headers.set('X-Impersonate-Token', parsed.token);
        }
      }
    } catch {
      // sessionStorage may be unavailable in some environments
    }
    // CB:END impersonation-token-header
    if (import.meta.env.DEV) {
      console.log('[useApi]', path.split('?')[0], { hasAuth: !!token });
    }

    // Handle body stringification
    let body = init.body;
    const isFormData = body instanceof FormData;
    
    // Only set Content-Type for non-FormData requests
    // FormData requests need the browser to set Content-Type with the boundary
    if (!isFormData) {
      headers.set("Content-Type", "application/json");
      if (body && typeof body === 'object') {
        body = JSON.stringify(body);
      }
    }
    // For FormData, don't set Content-Type - let the browser set it automatically
    
    // CB:BEGIN use-centralized-apiurl
    // Use centralized apiUrl function for consistent URL building
    // All paths are now canonical (/api/admin/*, /api/projects/*)
    // All paths are canonical (/api/admin/*, /api/projects/*)
    // Backend rewrite shim handles /api/dev/* → /api/* for backwards compatibility
    const { apiUrl } = await import('./getApiUrl');
    const url = apiUrl(path);
    // CB:END use-centralized-apiurl

    // CB:BEGIN api-unreachable-handling
    let res: Response;
    try {
      res = await fetch(url, { ...init, body, headers, credentials: 'include' });
    } catch (networkError: any) {
      if (networkError instanceof TypeError && (networkError.message === 'Failed to fetch' || networkError.message?.includes('fetch'))) {
        throw new Error('API_UNREACHABLE');
      }
      throw networkError;
    }
    // CB:END api-unreachable-handling
    
    // CB:BEGIN robust-401-handling
    // Handle 401 gracefully - don't spam logs, throw typed error
    if (!res.ok) {
      if (res.status === 401) {
        // Optional: log once in dev, quiet in prod
        if (import.meta.env.DEV) {
          console.warn('[useApi] 401 Unauthorized for', url);
        }
        throw new Error("UNAUTHORIZED");
      }
      
      // For other errors, get error message from response
      const text = await res.text();
      let errorMessage = text || `Request failed with status ${res.status}`;
      
      // Try to parse as JSON for structured errors
      try {
        const json = JSON.parse(text);
        if (res.status === 403 && json.code === 'REQUIRES_ACCEPTANCE') {
          throw new Error('LEGAL_ACCEPTANCE_REQUIRED');
        }
        errorMessage = json.error || json.message || errorMessage;
        if (json.details && String(json.details) !== errorMessage) {
          errorMessage = `${errorMessage}: ${json.details}`;
        }
      } catch (e: any) {
        if (e?.message === 'LEGAL_ACCEPTANCE_REQUIRED') throw e;
        // Not JSON, use text as-is
      }
      const err = new Error(errorMessage) as Error & { statusCode?: number };
      err.statusCode = res.status;
      throw err;
    }
    // CB:END robust-401-handling
    
    return res.json();
  }

  // Basic CRUD methods
  const get = async <T = any>(path: string, options?: RequestOptions): Promise<T> => request<T>(path, options);
  const post = async <T = any>(path: string, body?: any, options?: RequestOptions): Promise<T> => {
    // If body is FormData, pass it as-is. Otherwise stringify JSON
    const bodyString = body instanceof FormData 
      ? body 
      : (typeof body === 'string' ? body : JSON.stringify(body || {}));
    return request<T>(path, { ...options, method: "POST", body: bodyString });
  };
  const put = async <T = any>(path: string, body?: any): Promise<T> => {
    // Ensure body is properly stringified, but don't double-stringify
    const bodyString = typeof body === 'string' ? body : JSON.stringify(body || {});
    return request<T>(path, { method: "PUT", body: bodyString });
  };
  const patch = async <T = any>(path: string, body?: any): Promise<T> => {
    // Ensure body is properly stringified, but don't double-stringify
    const bodyString = typeof body === 'string' ? body : JSON.stringify(body || {});
    return request<T>(path, { method: "PATCH", body: bodyString });
  };
  // CB:BEGIN delete-with-body
  const del = async <T = any>(path: string, body?: any, options: RequestOptions = {}): Promise<T> => {
    let bodyString: any = undefined;
    if (typeof body !== "undefined") {
      bodyString = typeof body === "string" ? body : JSON.stringify(body || {});
    }
    return request<T>(path, {
      ...options,
      method: "DELETE",
      ...(bodyString !== undefined ? { body: bodyString } : {}),
    });
  };
  // CB:END delete-with-body

  // Legacy methods for backward compatibility
  const getUserProfile = async (): Promise<UserProfile> => {
    return request<UserProfile>("/api/me");
  };

  const getLatestReport = async (homeownerId: string) => {
    try {
      return await request(`/api/reports/latest/${homeownerId}`);
    } catch (error: any) {
      // Return null for 404 (no report found) instead of throwing
      if (error?.status === 404) {
        return null;
      }
      throw error;
    }
  };

  const getProjectPhases = async (projectId: string) => {
    return request(`/api/projects/${projectId}/phases`);
  };

  const homeownerAcceptPhase = async (projectId: string, phaseNumber: string | number) => {
    return request(`/api/projects/${projectId}/phases/${phaseNumber}/approve`, {
      method: "POST"
    });
  };

  const releasePhaseDeposit = async (projectId: string, phaseId: string) => {
    return request(`/api/projects/${projectId}/phases/${phaseId}/release`, {
      method: "POST"
    });
  };

  const saveBidDraft = async (projectId: string, bidData: any) => {
    return request(`/api/projects/${projectId}/bid-draft`, {
      method: "POST",
      body: JSON.stringify(bidData)
    });
  };

  const getBidDraft = async (projectId: string) => {
    // CB:BEGIN pass-userid-for-bid-draft
    const userId = (window as any).__clerk?.user?.id;
    const url = userId 
      ? `/api/projects/${String(projectId)}/bid-draft?userId=${encodeURIComponent(userId)}`
      : `/api/projects/${String(projectId)}/bid-draft`;
    try {
      return await request(url);
    } catch (e: any) {
      // 404 or missing bid-draft endpoint: treat as no draft (CB: graceful-404-bid-draft)
      if (e?.statusCode === 404 || e?.message === 'Not found' || /404|not found/i.test(String(e?.message ?? ''))) return { bid: null };
      throw e;
    }
    // CB:END pass-userid-for-bid-draft
  };

  // CB:BEGIN contractor-templates-api
  const listContractorTemplates = async (): Promise<Array<{ id: string; name: string; updatedAt: string }>> => {
    try {
      return await request('/api/contractor/quote-templates');
    } catch {
      return [];
    }
  };

  const getContractorTemplate = async (id: string): Promise<Record<string, unknown> | null> => {
    try {
      return await request(`/api/contractor/quote-templates/${id}`);
    } catch {
      return null;
    }
  };

  const createContractorTemplate = async (name: string, data: Record<string, unknown>): Promise<{ id: string; name: string } | null> => {
    try {
      return await request('/api/contractor/quote-templates', {
        method: 'POST',
        body: JSON.stringify({ name, data }),
      });
    } catch {
      return null;
    }
  };

  const deleteContractorTemplate = async (id: string): Promise<void> => {
    try {
      await request(`/api/contractor/quote-templates/${id}`, { method: 'DELETE' });
    } catch {
      // ignore
    }
  };
  // CB:END contractor-templates-api

  // CB:BEGIN contractor-payout-status-service
  const getContractorPayoutStatus = async (): Promise<{
    payoutReady: boolean;
    payoutReadyAt: string | null;
    stripeAccountId: string | null;
  }> => {
    return request('/api/contractor/payout-status');
  };
  // CB:END contractor-payout-status-service

  // CB:BEGIN platform-agreement-api
  const acceptPlatformAgreement = async (version?: number): Promise<{ ok: boolean; version: string; acceptedAt: string; alreadyAccepted?: boolean }> => {
    return request('/api/compliance/accept-platform-agreement', {
      method: 'POST',
      body: JSON.stringify({ version: String(version ?? 1) }),
    });
  };

  const getPlatformAgreementStatus = async (): Promise<{
    currentVersion: string;
    acceptedVersion: string | null;
    acceptedAt: string | null;
    requiresAcceptance: boolean;
  }> => {
    // Use the central request() function so impersonation + auth headers are injected automatically
    const fallback = { currentVersion: '1', acceptedVersion: null, acceptedAt: null, requiresAcceptance: true };
    try {
      const data = await request<typeof fallback>('/api/compliance/platform-agreement-status');
      return data;
    } catch {
      return fallback;
    }
  };
  // CB:END platform-agreement-api

  return {
    request,
    get,
    post,
    put,
    patch,
    del,
    delete: del, // Alias for delete method using bracket notation
    getUserProfile,
    getLatestReport,
    getProjectPhases,
    homeownerAcceptPhase,
    releasePhaseDeposit,
    saveBidDraft,
    getBidDraft,
    getContractorPayoutStatus,
    listContractorTemplates,
    getContractorTemplate,
    createContractorTemplate,
    deleteContractorTemplate,
    acceptPlatformAgreement,
    getPlatformAgreementStatus,
  };
}
// CB:END modern-api-wrapper